/**
 * Session-scoped "verified" latch for the email-verification gate (ENG-3797
 * flicker fix).
 *
 * WHY this exists (the confirmed flicker root cause): the gate's verified
 * stickiness used to live ONLY in `useVerificationSignal`'s component-local
 * `useState` (`liveClaims`). That guards out-of-order /userinfo responses within
 * a single mount, but it is LOST the instant the gate REMOUNTS. After the gate
 * first dismisses, the app shell mounts and `ProfileSynchronizer` can briefly
 * flip back to its `<Loader/>` branch (e.g. registry churn re-evaluates
 * `hasStudioUser`), which unmounts + remounts the gate. On remount `liveClaims`
 * resets to `undefined`, so `claims` falls back to the Auth0 SDK `user` — which,
 * for a custom-DB user, stays `email_verified:false` on the memory-cached token —
 * and the signal reverts to `unverified` → the PENDING screen for ~one poll
 * interval (a couple seconds) until the next /userinfo poll flips it true again.
 * That is the observed pass → revert-to-pending → pass flicker.
 *
 * The fix: once any live signal observes `email_verified:true` for an identity,
 * latch it OUTSIDE component state so a remount cannot lose it. A genuinely
 * verified email never un-verifies mid-session, so the latch is monotonic and
 * safe. The latch is:
 *   - keyed by the verified IDENTITY = Auth0 `sub` + `email`, NOT `sub` alone
 *     (codex round-2 finding). A user can change their email in account settings
 *     while keeping the same `sub`; the backend then clears the verification
 *     timestamp and `email_verified` becomes false for the NEW email
 *     (api/.../users/update/main.go). Keying on `sub` alone would keep granting
 *     verified for the new, unverified email (sessionStorage survives the
 *     change-email logout within the same tab). Including the email in the key
 *     means a changed email no longer matches the old latch, so the new
 *     unverified email is correctly gated.
 *   - keyed by identity ALSO means it can never leak a verified state across a
 *     different user or a logout+login as someone else (different `sub`);
 *   - backed by BOTH a module-level Set (survives remount within the SPA
 *     lifetime, no I/O on the hot path) AND `sessionStorage` (survives a
 *     same-tab full reload, and is automatically dropped when the tab closes —
 *     it must NOT be `localStorage`, which would persist a verified claim
 *     indefinitely across unrelated future sessions);
 *   - set ONLY on an explicit `email_verified:true` — an unknown/errored signal
 *     never latches, preserving the gate's fail-closed-on-unknown contract.
 */

/** The identity a verified observation is latched against (spec: sub + email). */
export interface VerifiedIdentity {
  /** Auth0 `sub` claim. */
  sub: string | undefined;
  /** `email` claim — part of the key so a same-`sub` email change re-gates. */
  email: string | undefined;
}

const STORAGE_PREFIX = 'iris.evg.verified.';

/**
 * In-memory record of verified identities observed this SPA lifetime. The
 * primary guard against a remount: it has no I/O cost and is consulted on every
 * render. `sessionStorage` is the durable backstop for a full same-tab reload.
 */
const verifiedIdentities = new Set<string>();

/**
 * Build the stable key for an identity, or `undefined` when the identity is not
 * complete enough to latch. Both `sub` and `email` are required: an unknown
 * subject must never latch (it would risk leaking across users), and omitting
 * the email would re-introduce the same-`sub` email-change over-grant. A NUL
 * (`\u0000`) separates the two parts; a NUL cannot appear inside an Auth0 `sub`
 * or an email address, so two distinct identities can never collide into one
 * key (a printable delimiter could, in theory, if a value contained it).
 */
const identityKey = (identity: VerifiedIdentity): string | undefined => {
  if (
    identity.sub === undefined ||
    identity.sub === '' ||
    identity.email === undefined ||
    identity.email === ''
  ) {
    return undefined;
  }
  return `${identity.sub}\u0000${identity.email}`;
};

/** sessionStorage key for an identity key. Namespaced so it can't collide with other state. */
const storageKey = (key: string): string => `${STORAGE_PREFIX}${key}`;

/**
 * Latch an identity as verified. No-op when the identity is incomplete (missing
 * `sub` or `email`) so an unknown identity never latches. sessionStorage
 * failures (private mode, quota, disabled) degrade silently to the in-memory
 * Set, which still defeats the remount flicker for the common case.
 */
export const latchVerified = (identity: VerifiedIdentity): void => {
  const key = identityKey(identity);
  if (key === undefined) {
    return;
  }
  verifiedIdentities.add(key);
  try {
    globalThis.sessionStorage?.setItem(storageKey(key), '1');
  } catch {
    // Best-effort persistence; the in-memory Set is the load-bearing guard.
  }
};

/**
 * Whether this exact identity (sub + email) has been observed verified this
 * session. Reads the in-memory Set first, then falls back to sessionStorage
 * (rehydrating the Set on a hit so a post-reload first render is already
 * authoritative). Always false for an incomplete identity so the latch can
 * never grant a different/anonymous user or a changed-email identity.
 */
export const isLatchedVerified = (identity: VerifiedIdentity): boolean => {
  const key = identityKey(identity);
  if (key === undefined) {
    return false;
  }
  if (verifiedIdentities.has(key)) {
    return true;
  }
  try {
    if (globalThis.sessionStorage?.getItem(storageKey(key)) === '1') {
      verifiedIdentities.add(key);
      return true;
    }
  } catch {
    // Treat an unreadable store as "not latched" — fail-closed, never grant.
  }
  return false;
};

/**
 * Test-only reset of the module-level latch. Exported so the gate's regression
 * suite can isolate the remount scenario per test; production code never calls it.
 */
export const __resetVerifiedLatchForTests = (): void => {
  verifiedIdentities.clear();
};
